import { useCallback, useMemo, useRef, useState } from "react"
import type { SortingState } from "@tanstack/react-table"
import type { CrudFilterState, CrudPeriod } from "../types/crudFilters.ts"

const CACHE_TTL_MS = 5 * 60 * 1000

export type CrudListSessionState = {
  pageIndex: number
  pageSize: number
  search: string
  sorting: SortingState
  filterState: CrudFilterState
  period: CrudPeriod
  scrollY: number
}

type CrudListSessionCache<T> = CrudListSessionState & {
  data: T[]
  totalCount: number
  stats: Record<string, number>
  fetchedAt: number
}

function readCache<T>(storageKey: string): CrudListSessionCache<T> | null {
  try {
    const raw = sessionStorage.getItem(storageKey)
    if (!raw) return null
    const parsed = JSON.parse(raw) as CrudListSessionCache<T>
    if (!parsed?.fetchedAt || Date.now() - parsed.fetchedAt > CACHE_TTL_MS) {
      sessionStorage.removeItem(storageKey)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export function useCrudListSession<T>(storageKey: string) {
  const initial = useMemo(() => readCache<T>(storageKey), [storageKey])
  const scrollRef = useRef<HTMLDivElement | null>(null)

  const [pageIndex, setPageIndex] = useState(initial?.pageIndex ?? 0)
  const [pageSize, setPageSize] = useState(initial?.pageSize ?? 20)
  const [search, setSearch] = useState(initial?.search ?? "")
  const [sorting, setSorting] = useState<SortingState>(initial?.sorting ?? [])
  const [filterState, setFilterState] = useState<CrudFilterState>(initial?.filterState ?? {})
  const [period, setPeriod] = useState<CrudPeriod>(initial?.period ?? null)
  const [cachedData, setCachedData] = useState<T[] | null>(initial?.data ?? null)
  const [cachedTotal, setCachedTotal] = useState<number | null>(initial?.totalCount ?? null)
  const [cachedStats, setCachedStats] = useState<Record<string, number> | null>(initial?.stats ?? null)

  const persist = useCallback(
    (payload: Partial<CrudListSessionCache<T>>) => {
      try {
        const existing = readCache<T>(storageKey)
        const next: CrudListSessionCache<T> = {
          pageIndex,
          pageSize,
          search,
          sorting,
          filterState,
          period,
          scrollY: scrollRef.current?.scrollTop ?? existing?.scrollY ?? 0,
          data: payload.data ?? existing?.data ?? cachedData ?? [],
          totalCount: payload.totalCount ?? existing?.totalCount ?? cachedTotal ?? 0,
          stats: payload.stats ?? existing?.stats ?? cachedStats ?? {},
          fetchedAt: payload.fetchedAt ?? Date.now(),
          ...payload,
        }
        sessionStorage.setItem(storageKey, JSON.stringify(next))
      } catch {
        // sessionStorage may be unavailable
      }
    },
    [storageKey, pageIndex, pageSize, search, sorting, filterState, period, cachedData, cachedTotal, cachedStats],
  )

  const saveScroll = useCallback(() => {
    persist({ scrollY: scrollRef.current?.scrollTop ?? 0 })
  }, [persist])

  const restoreScroll = useCallback(() => {
    const cache = readCache<T>(storageKey)
    if (cache?.scrollY && scrollRef.current) {
      requestAnimationFrame(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = cache.scrollY
      })
    }
  }, [storageKey])

  const applyCacheToState = useCallback(
    (cache: CrudListSessionCache<T>) => {
      setCachedData(cache.data)
      setCachedTotal(cache.totalCount)
      setCachedStats(cache.stats)
    },
    [],
  )

  const clearCache = useCallback(() => {
    sessionStorage.removeItem(storageKey)
    setCachedData(null)
    setCachedTotal(null)
    setCachedStats(null)
  }, [storageKey])

  return {
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    search,
    setSearch,
    sorting,
    setSorting,
    filterState,
    setFilterState,
    period,
    setPeriod,
    cachedData,
    cachedTotal,
    cachedStats,
    setCachedData,
    setCachedTotal,
    setCachedStats,
    persist,
    saveScroll,
    restoreScroll,
    applyCacheToState,
    clearCache,
    scrollRef,
    hadInitialCache: Boolean(initial),
  }
}
